import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, TextField, Typography, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import api from '../services/api';

const MESES_CURTO = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const LIMITE_MESES = 60;

const chaveDoMes = (ano, mes) => `${ano}-${mes}`;

// "2026-10-01" -> {ano, mes}. Vazio ou inválido -> null.
function lerData(texto) {
  const m = /^(\d{4})-(\d{2})/.exec(texto || '');
  return m ? { ano: Number(m[1]), mes: Number(m[2]) } : null;
}

// Meses do período, do início ao fim (inclusive). Passa de 60 meses? Corta (o aviso aparece na tela).
export function mesesEntre(dataInicio, dataFim) {
  const ini = lerData(dataInicio);
  const fim = lerData(dataFim);
  if (!ini || !fim) return [];
  const lista = [];
  let { ano, mes } = ini;
  while ((ano < fim.ano || (ano === fim.ano && mes <= fim.mes)) && lista.length < LIMITE_MESES) {
    lista.push({ ano, mes });
    mes += 1;
    if (mes === 13) { mes = 1; ano += 1; }
  }
  return lista;
}

const comoNumero = (texto) => {
  const t = String(texto ?? '').trim().replace(',', '.');
  if (t === '') return 0;
  return Number(t);
};

// Confere um valor digitado: zero ou mais, dias inteiros ou meio dia. Devolve a mensagem de erro ou ''.
export function erroDoValor(texto) {
  const n = comoNumero(texto);
  if (Number.isNaN(n)) return 'use só números';
  if (n < 0) return 'não pode ser negativo';
  if (n > 999) return 'valor alto demais';
  if ((n * 2) % 1 !== 0) return 'use dias inteiros ou meio dia (ex.: 2 ou 2,5)';
  return '';
}

// valores: { '2026-10': { consultoria: '12', workshop: '2' } }  ->  lista para a API (só dos meses informados).
export function paraLista(meses, valores) {
  return meses.map(({ ano, mes }) => {
    const v = valores[chaveDoMes(ano, mes)] || {};
    return { ano, mes, consultoria: comoNumero(v.consultoria), workshop: comoNumero(v.workshop) };
  });
}

// Primeiro erro da grade (ou ''), no formato "Out/2026, Consultoria: use dias inteiros...".
export function primeiroErro(meses, valores) {
  for (const { ano, mes } of meses) {
    const v = valores[chaveDoMes(ano, mes)] || {};
    for (const [campo, rotulo] of [['consultoria', 'Consultoria'], ['workshop', 'Workshop']]) {
      const e = erroDoValor(v[campo]);
      if (e) return `${MESES_CURTO[mes - 1]}/${ano}, ${rotulo}: ${e}.`;
    }
  }
  return '';
}

const formatar = (n) => Number(n || 0).toLocaleString('pt-BR', { maximumFractionDigits: 1 });

// Grade "mês a mês": uma linha por mês, com os dias de Consultoria e de Workshop.
export function PlanejamentoGrade({ meses, valores, aoMudar, desabilitado = false }) {
  const totais = meses.reduce((acc, { ano, mes }) => {
    const v = valores[chaveDoMes(ano, mes)] || {};
    const c = comoNumero(v.consultoria);
    const w = comoNumero(v.workshop);
    return {
      consultoria: acc.consultoria + (Number.isNaN(c) ? 0 : c),
      workshop: acc.workshop + (Number.isNaN(w) ? 0 : w),
    };
  }, { consultoria: 0, workshop: 0 });

  const campo = (ano, mes, nome, rotulo) => {
    const valor = (valores[chaveDoMes(ano, mes)] || {})[nome] ?? '';
    const erro = erroDoValor(valor);
    return (
      <TextField
        size="small" value={valor} disabled={desabilitado} error={Boolean(erro)}
        placeholder="0"
        onChange={(e) => aoMudar(ano, mes, nome, e.target.value)}
        slotProps={{ htmlInput: { inputMode: 'decimal', 'aria-label': `${rotulo} em ${MESES_CURTO[mes - 1]} de ${ano}`, style: { textAlign: 'center' } } }}
        sx={{ width: 90 }}
      />
    );
  };

  return (
    <Table size="small" sx={{ '& td, & th': { borderColor: '#e5e7eb' } }}>
      <TableHead>
        <TableRow sx={{ backgroundColor: '#f3f4f6' }}>
          <TableCell sx={{ fontWeight: 700 }}>Mês</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Consultoria (dias)</TableCell>
          <TableCell align="center" sx={{ fontWeight: 700 }}>Workshop (dias)</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {meses.map(({ ano, mes }) => (
          <TableRow key={chaveDoMes(ano, mes)}>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{MESES_CURTO[mes - 1]}/{ano}</TableCell>
            <TableCell align="center">{campo(ano, mes, 'consultoria', 'Consultoria')}</TableCell>
            <TableCell align="center">{campo(ano, mes, 'workshop', 'Workshop')}</TableCell>
          </TableRow>
        ))}
        <TableRow sx={{ backgroundColor: '#f9fafb' }}>
          <TableCell sx={{ fontWeight: 800 }}>Total planejado</TableCell>
          <TableCell align="center" sx={{ fontWeight: 800 }}>{formatar(totais.consultoria)}</TableCell>
          <TableCell align="center" sx={{ fontWeight: 800 }}>{formatar(totais.workshop)}</TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}

// Trecho do formulário "Adicionar projeto": os meses vêm das datas de início e de fim.
export function PlanejamentoNoCadastro({ dataInicio, dataFim, valores, aoMudar }) {
  const meses = useMemo(() => mesesEntre(dataInicio, dataFim), [dataInicio, dataFim]);
  const passou = lerData(dataInicio) && lerData(dataFim) && meses.length >= LIMITE_MESES;
  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Planejamento da carga</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Dias de consultoria e de workshop planejados para cada mês do projeto. Aparece na tabela “Carga por mês de cada projeto” do Controle de Carga.
      </Typography>
      {meses.length === 0 ? (
        <Alert severity="info">Informe a data início e a data fim para planejar a carga mês a mês.</Alert>
      ) : (
        <>
          {passou && <Alert severity="warning" sx={{ mb: 1 }}>Mostrando os primeiros {LIMITE_MESES} meses do período.</Alert>}
          <PlanejamentoGrade meses={meses} valores={valores} aoMudar={aoMudar} />
        </>
      )}
    </Box>
  );
}

// Carrega e grava o planejamento de um projeto. Usado na aba Carga e dentro do EDITAR do projeto.
function usePlanejamento(projetoId, dataInicio, dataFim) {
  const [carregado, setCarregado] = useState(false);
  const [erroCarga, setErroCarga] = useState('');
  const [podeEditar, setPodeEditar] = useState(false);
  const [valores, setValores] = useState({});
  const [extras, setExtras] = useState([]);              // meses que a pessoa acrescentou fora do período

  const aplicar = (dados) => {
    const mapa = {};
    dados.planejamento.forEach((p) => {
      mapa[chaveDoMes(p.ano, p.mes)] = {
        consultoria: p.consultoria ? String(p.consultoria).replace('.', ',') : '',
        workshop: p.workshop ? String(p.workshop).replace('.', ',') : '',
      };
    });
    setValores(mapa);
    setPodeEditar(Boolean(dados.pode_editar));
    setExtras(dados.planejamento.map((p) => ({ ano: p.ano, mes: p.mes })));
  };

  useEffect(() => {
    let ativo = true;
    setCarregado(false);
    setErroCarga('');
    api.get(`/projetos/${projetoId}/planejamento/`)
      .then((r) => { if (ativo) { aplicar(r.data); setCarregado(true); } })
      .catch((e) => {
        if (!ativo) return;
        const status = e?.response?.status;
        setErroCarga(status ? `erro ${status}` : 'sem resposta do servidor');
      });
    return () => { ativo = false; };
  }, [projetoId]);

  const meses = useMemo(() => {
    const todos = new Map();
    [...mesesEntre(dataInicio, dataFim), ...extras].forEach((m) => todos.set(chaveDoMes(m.ano, m.mes), m));
    return [...todos.values()].sort((a, b) => (a.ano - b.ano) || (a.mes - b.mes));
  }, [dataInicio, dataFim, extras]);

  const mudar = (ano, mes, campo, texto) => {
    setValores((v) => ({ ...v, [chaveDoMes(ano, mes)]: { ...(v[chaveDoMes(ano, mes)] || {}), [campo]: texto } }));
  };

  const acrescentar = (texto) => {
    const m = lerData(texto);
    if (!m) return;
    setExtras((e) => (e.some((x) => x.ano === m.ano && x.mes === m.mes) ? e : [...e, m]));
  };

  const validar = () => primeiroErro(meses, valores);

  // Grava no servidor. Devolve '' se deu certo ou a mensagem de erro.
  const gravar = async () => {
    try {
      const r = await api.put(`/projetos/${projetoId}/planejamento/`, { planejamento: paraLista(meses, valores) });
      aplicar(r.data);
      return '';
    } catch (e) {
      const d = e?.response?.data;
      const texto = d && typeof d.detail === 'string' ? d.detail : null;
      return texto || 'Não foi possível salvar o planejamento. Tente de novo; se continuar, avise o Admin.';
    }
  };

  return { carregado, erroCarga, podeEditar, valores, meses, mudar, acrescentar, validar, gravar };
}

function AvisoDoPlanejamento({ plano }) {
  if (plano.erroCarga) {
    return <Alert severity="error" sx={{ mb: 2 }}>Não foi possível carregar o planejamento da carga ({plano.erroCarga}). Atualize a página; se continuar, avise o Admin.</Alert>;
  }
  if (!plano.carregado) return <Typography variant="body2" color="text.secondary">Carregando o planejamento...</Typography>;
  return null;
}

// Aba Carga do Projeto Detalhe: mostra o planejamento; quem tem a regra "Editar projeto" também altera.
export default function PlanejamentoProjeto({ projetoId, dataInicio, dataFim }) {
  const plano = usePlanejamento(projetoId, dataInicio, dataFim);
  const [novoMes, setNovoMes] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [mensagem, setMensagem] = useState(null);        // { tipo, texto }

  const salvar = async () => {
    setMensagem(null);
    const erro = plano.validar();
    if (erro) { setMensagem({ tipo: 'error', texto: erro }); return; }
    setSalvando(true);
    const falha = await plano.gravar();
    setMensagem(falha ? { tipo: 'error', texto: falha } : { tipo: 'success', texto: 'Planejamento salvo. Já aparece no Controle de Carga.' });
    setSalvando(false);
  };

  return (
    <Box sx={{ mt: 3, p: 2.5, border: '1px solid #e5e7eb', borderRadius: 3, backgroundColor: 'white' }}>
      <Typography sx={{ fontWeight: 700 }}>Planejamento da carga mês a mês</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Dias de consultoria e de workshop planejados para o projeto. É o que aparece na tabela “Carga por mês de cada projeto” do Controle de Carga.
        {plano.carregado && !plano.podeEditar && ' Só quem tem permissão de editar o projeto pode alterar.'}
      </Typography>
      <AvisoDoPlanejamento plano={plano} />
      {plano.carregado && (
        <>
          {mensagem && <Alert severity={mensagem.tipo} sx={{ mb: 2 }} onClose={() => setMensagem(null)}>{mensagem.texto}</Alert>}
          {plano.meses.length === 0 ? (
            <Alert severity="info">
              Este projeto ainda não tem planejamento.
              {plano.podeEditar ? ' Escolha um mês abaixo para começar, ou preencha a data início e a data fim do projeto.' : ''}
            </Alert>
          ) : (
            <PlanejamentoGrade
              meses={plano.meses} valores={plano.valores} desabilitado={!plano.podeEditar || salvando}
              aoMudar={(ano, mes, campo, texto) => { setMensagem(null); plano.mudar(ano, mes, campo, texto); }}
            />
          )}
          {plano.podeEditar && (
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 2 }}>
              <TextField
                size="small" type="month" label="Acrescentar mês" value={novoMes}
                onChange={(e) => setNovoMes(e.target.value)}
                slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 190 }}
              />
              <Button
                variant="outlined" disabled={!novoMes || salvando} sx={{ textTransform: 'none' }}
                onClick={() => { plano.acrescentar(novoMes); setNovoMes(''); }}
              >
                Acrescentar
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button
                variant="contained" startIcon={<SaveIcon />} onClick={salvar} disabled={salvando}
                sx={{ backgroundColor: '#13375F', textTransform: 'none' }}
              >
                {salvando ? 'Salvando...' : 'Salvar planejamento'}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

// Trecho do EDITAR do projeto: a mesma grade, mas quem salva é o botão Salvar do diálogo.
// O diálogo chama registrar({ validar, gravar }) para validar e gravar o planejamento junto com o resto.
export function PlanejamentoNoEditar({ projetoId, dataInicio, dataFim, registrar }) {
  const plano = usePlanejamento(projetoId, dataInicio, dataFim);
  const { validar, gravar } = plano;
  const [novoMes, setNovoMes] = useState('');

  useEffect(() => {
    registrar({ validar, gravar, editavel: plano.carregado && plano.podeEditar });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plano.carregado, plano.podeEditar, plano.valores, plano.meses]);

  return (
    <Box>
      <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Planejamento da carga</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Dias de consultoria e de workshop de cada mês. Os meses seguem a data início e a data fim acima.
      </Typography>
      <AvisoDoPlanejamento plano={plano} />
      {plano.carregado && (plano.meses.length === 0 ? (
        <Alert severity="info">Informe a data início e a data fim para planejar a carga mês a mês.</Alert>
      ) : (
        <PlanejamentoGrade meses={plano.meses} valores={plano.valores} aoMudar={plano.mudar} desabilitado={!plano.podeEditar} />
      ))}
      {plano.carregado && plano.podeEditar && (
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mt: 1.5 }}>
          <TextField
            size="small" type="month" label="Acrescentar mês" value={novoMes}
            onChange={(e) => setNovoMes(e.target.value)}
            slotProps={{ inputLabel: { shrink: true } }} sx={{ width: 190 }}
          />
          <Button variant="outlined" disabled={!novoMes} sx={{ textTransform: 'none' }} onClick={() => { plano.acrescentar(novoMes); setNovoMes(''); }}>
            Acrescentar
          </Button>
        </Box>
      )}
    </Box>
  );
}
